import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronUp, HelpCircle, DollarSign, Globe, Ticket, CheckCircle, Search, ArrowRight, Shield, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { faqTranslations } from '../i18n/faqTranslations';
import { API } from '../App';

const FAQPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dynamicFaqs, setDynamicFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  
  const t = faqTranslations[language] || faqTranslations.fr;

  // Initialize activeCategory based on hash, then allow user to change it
  const getInitialCategory = () => {
    const hash = location.hash.replace('#', '');
    if (hash === 'tarifs') return 'payment';
    return 'all';
  };
  
  const [activeCategory, setActiveCategory] = useState(getInitialCategory);

  // Mapping between UI category IDs and API category names
  // UI uses English IDs, API stores categories in French
  const categoryToApiMap = {
    'all': 'all',
    'general': 'general',
    'fraud': 'securite',
    'process': 'support',
    'payment': 'paiement',
    'international': 'international',
    'tickets': 'tickets'
  };

  // Get all FAQs for a specific category - merges dynamic and static FAQs
  const getFaqsForCategory = (category) => {
    const categoryToUse = category || 'all';
    const apiCategory = categoryToApiMap[categoryToUse] || categoryToUse;
    const staticFaqs = t.faqs;
    
    // Helper to get static FAQs for a category
    const getStaticFaqsForCat = (cat) => {
      return staticFaqs[cat]?.map(faq => ({ ...faq, category: cat })) || [];
    };
    
    // Get dynamic FAQs
    let dynamicFaqsList = [];
    if (dynamicFaqs.length > 0) {
      dynamicFaqsList = dynamicFaqs.map(faq => ({
        question: language === 'fr' ? faq.question_fr : (faq.question_en || faq.question_fr),
        answer: language === 'fr' ? faq.answer_fr : (faq.answer_en || faq.answer_fr),
        category: faq.category,
        id: faq.id,
        isDynamic: true
      }));
      
      if (categoryToUse !== 'all') {
        dynamicFaqsList = dynamicFaqsList.filter(faq => faq.category === apiCategory);
      }
    }
    
    // Get static FAQs
    let staticFaqsList = [];
    if (categoryToUse === 'all') {
      Object.keys(staticFaqs).forEach(cat => {
        staticFaqs[cat]?.forEach(faq => {
          staticFaqsList.push({ ...faq, category: cat, isDynamic: false });
        });
      });
    } else {
      staticFaqsList = getStaticFaqsForCat(categoryToUse).map(faq => ({ ...faq, isDynamic: false }));
    }
    
    // Merge: Dynamic FAQs first, then static FAQs (avoiding duplicates by question text)
    const dynamicQuestions = new Set(dynamicFaqsList.map(f => f.question.toLowerCase()));
    const mergedFaqs = [
      ...dynamicFaqsList,
      ...staticFaqsList.filter(faq => !dynamicQuestions.has(faq.question.toLowerCase()))
    ];
    
    return mergedFaqs;
  };

  // Handle hash navigation - set category and open specific FAQ when navigating to #tarifs
  useEffect(() => {
    if (!loading) {
      const hash = location.hash.replace('#', '');
      if (hash === 'tarifs') {
        setActiveCategory('payment');
        // Find and open the tarifs question
        const paymentFaqs = getFaqsForCategory('payment');
        const tarifIndex = paymentFaqs.findIndex(
          faq => faq.id === 'tarifs' || faq.question.toLowerCase().includes('frais de transfert')
        );
        if (tarifIndex !== -1) {
          setOpenIndex(tarifIndex);
          // Scroll to element after render
          setTimeout(() => {
            const element = document.getElementById('faq-tarifs');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 150);
        }
      }
    }
  }, [loading, location.hash]);

  const categories = [
    { id: 'all', label: language === 'fr' ? 'Toutes' : 'All', icon: HelpCircle },
    { id: 'general', label: language === 'fr' ? 'Général' : 'General', icon: HelpCircle },
    { id: 'fraud', label: language === 'fr' ? 'Fraude & Sécurité' : 'Fraud & Security', icon: Shield },
    { id: 'process', label: language === 'fr' ? 'Processus' : 'Process', icon: CheckCircle },
    { id: 'payment', label: language === 'fr' ? 'Paiement' : 'Payment', icon: DollarSign },
    { id: 'international', label: 'International', icon: Globe },
    { id: 'tickets', label: language === 'fr' ? 'Billets' : 'Tickets', icon: Ticket },
  ];

  // Fetch FAQs from API
  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setDynamicFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Keep dynamicFaqs empty, will fallback to static
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  // Compute filtered FAQs based on active category and search query
  const filteredFaqs = useMemo(() => {
    const faqs = getFaqsForCategory(activeCategory);
    if (!searchQuery) return faqs;
    const query = searchQuery.toLowerCase();
    return faqs.filter(faq => 
      faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
    );
  }, [activeCategory, searchQuery, dynamicFaqs, language, t.faqs]);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      {/* Spacer for fixed navbar */}
      <div className="h-16 md:h-20"></div>

      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-800 text-white py-10 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <HelpCircle className="h-12 w-12 mx-auto mb-4 text-brand-200" />
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            {language === 'fr' ? 'Questions fréquentes' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-base md:text-xl text-brand-100 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Trouvez rapidement des réponses à vos questions'
              : 'Find answers to your questions quickly'}
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border-b py-4 md:py-6 sticky top-16 md:top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder={language === 'fr' ? 'Rechercher une question...' : 'Search for a question...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 text-base md:text-lg rounded-full border-2 focus:border-brand-500"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Sidebar - Categories */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-40">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm md:text-base">
                  {language === 'fr' ? 'Catégories' : 'Categories'}
                </h3>
                <div className="flex flex-wrap lg:flex-col gap-2">
                  {categories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => {
                          setActiveCategory(cat.id);
                          setOpenIndex(null);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                          activeCategory === cat.id
                            ? 'bg-brand-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{cat.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Link to Fraud Stats Page */}
                <div className="mt-6 p-4 bg-[#67ffbb]/10 border border-[#67ffbb]/30 rounded-xl">
                  <Ticket className="h-6 w-6 text-[#2eb382] mb-2" />
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">
                    {language === 'fr' ? 'Guides de transfert' : 'Transfer Guides'}
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {language === 'fr' 
                      ? 'Comment transférer vos billets sur chaque plateforme.'
                      : 'How to transfer tickets on each platform.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/platform-guides')}
                    className="w-full border-[#67ffbb] text-[#2eb382] hover:bg-[#67ffbb]/10 text-xs"
                  >
                    {language === 'fr' ? 'Voir les guides' : 'View guides'}
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>

                {/* Link to Fraud Stats Page */}
                <div className="mt-4 p-4 bg-[#67ffbb]/10 border border-[#67ffbb]/30 rounded-xl">
                  <Shield className="h-6 w-6 text-[#2eb382] mb-2" />
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">
                    {language === 'fr' ? 'Statistiques de fraude' : 'Fraud Statistics'}
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {language === 'fr' 
                      ? 'Découvrez les risques et comment vous protéger.'
                      : 'Learn about the risks and how to protect yourself.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/fraud-statistics')}
                    className="w-full border-[#67ffbb] text-[#2eb382] hover:bg-[#67ffbb]/10 text-xs"
                  >
                    {language === 'fr' ? 'En savoir plus' : 'Learn more'}
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>

            {/* FAQ List */}
            <div className="flex-1 max-w-3xl">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
                </div>
              ) : filteredFaqs.length === 0 ? (
                <div className="text-center py-12">
                  <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {language === 'fr' 
                      ? 'Aucune question trouvée pour votre recherche.'
                      : 'No questions found for your search.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredFaqs.map((faq, index) => (
                    <div 
                      key={index}
                      id={faq.id === 'tarifs' || faq.question.toLowerCase().includes('frais de transfert') ? 'faq-tarifs' : undefined}
                      className={`bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow ${
                        faq.id === 'tarifs' && openIndex === index ? 'ring-2 ring-brand-500' : ''
                      }`}
                    >
                      <button
                        onClick={() => toggleFAQ(index)}
                        className="w-full px-4 md:px-6 py-4 md:py-5 text-left flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 md:w-8 md:h-8 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center font-bold text-xs md:text-sm flex-shrink-0">
                            Q
                          </span>
                          <span className="font-medium text-gray-900 text-sm md:text-base">{faq.question}</span>
                        </div>
                        {openIndex === index ? (
                          <ChevronUp className="h-5 w-5 text-brand-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openIndex === index && (
                        <div className="px-4 md:px-6 pb-4 md:pb-5 border-t border-gray-100">
                          <div className="flex items-start gap-3 pt-4">
                            <span className="w-6 h-6 md:w-8 md:h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-xs md:text-sm flex-shrink-0">
                              R
                            </span>
                            <div className="text-gray-600 text-sm md:text-base whitespace-pre-line leading-relaxed break-words overflow-hidden" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {faq.answer}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Results count */}
              {!loading && filteredFaqs.length > 0 && (
                <p className="text-center text-gray-500 text-sm mt-6">
                  {filteredFaqs.length} {language === 'fr' ? 'question(s) trouvée(s)' : 'question(s) found'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-brand-700 to-brand-900 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-3xl font-bold mb-3 md:mb-4">
            {language === 'fr' ? 'Vous avez d\'autres questions?' : 'Still have questions?'}
          </h2>
          <p className="text-brand-200 mb-6 md:mb-8 max-w-2xl mx-auto text-sm md:text-base">
            {language === 'fr' 
              ? 'Notre équipe est là pour vous aider. Contactez-nous pour une assistance personnalisée.'
              : 'Our team is here to help. Contact us for personalized assistance.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-brand-700 hover:bg-brand-50 px-6 py-3"
            >
              {language === 'fr' ? 'Nous contacter' : 'Contact Us'}
            </Button>
            <Button 
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-6 py-3"
            >
              {language === 'fr' ? 'Commencer' : 'Get Started'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
